import { AfterViewInit, Directive, ElementRef, inject, OnDestroy } from '@angular/core';
import { IconState } from './icon-registry';
import { IconHelperService } from './icon-helper.service';

@Directive({
  selector: 'i.icons, span.icons, [iconHelper]',
})
export class IconHelperDirective implements AfterViewInit, OnDestroy {
  private observer?: MutationObserver;
  private interactiveObserver?: MutationObserver;
  private interactiveAncestor?: HTMLElement | null;
  private cleanupFns: Array<() => void> = [];
  private lastRenderedSvg: string | null = null;
  private suppressFocusSelectedState = false;
  private readonly interactiveSelector = [
    '.app-button',
    '[appButton]',
    '[appIconButton]',
    ':where(button, label, a).primary',
    ':where(button, label, a).ghost',
    ':where(button, label, a).secondary',
    ':where(button, label, a).danger',
    ':where(button, label, a).add-btn',
    ':where(button, label, a).action-btn',
  ].join(', ');
  private readonly iconHelperService = inject(IconHelperService);
  private readonly host = inject(ElementRef<HTMLElement>);

  ngAfterViewInit(): void {
    this.bindInteractiveAncestor();
    this.renderIcon();
    this.observer = new MutationObserver(() => {
      this.bindInteractiveAncestor();
      this.renderIcon();
    });
    this.observer.observe(this.host.nativeElement, {
      attributes: true,
      attributeFilter: ['class', 'data-icon-state'],
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.interactiveObserver?.disconnect();
    this.cleanupFns.forEach((cleanup) => cleanup());
    this.cleanupFns = [];
  }

  private renderIcon(): void {
    const element = this.host.nativeElement;
    const iconName = this.getIconName(element);
    if (!iconName) {
      if (element.innerHTML !== '') {
        element.innerHTML = '';
      }
      this.lastRenderedSvg = null;
      return;
    }
    const state = this.getState(element, iconName);
    const svg = this.iconHelperService.get(iconName, state);
    if (!svg) {
      if (element.innerHTML !== '') {
        element.innerHTML = '';
      }
      this.lastRenderedSvg = null;
      return;
    }
    if (this.lastRenderedSvg !== svg) {
      element.innerHTML = svg;
      this.lastRenderedSvg = svg;
    }
    element.setAttribute('aria-hidden', 'true');
  }

  private getIconName(element: HTMLElement): string | null {
    for (const className of Array.from(element.classList)) {
      if (!className.startsWith('icon-')) {
        continue;
      }
      const iconName = className.slice('icon-'.length).trim();
      if (iconName && this.iconHelperService.has(iconName)) {
        return iconName;
      }
    }
    return null;
  }

  private getState(element: HTMLElement, iconName: string): IconState {
    const stateAttr = element.getAttribute('data-icon-state');
    if (stateAttr === 'default' || stateAttr === 'selected') {
      return stateAttr;
    }
    if (
      element.classList.contains('is-selected') ||
      element.classList.contains('state-selected') ||
      element.classList.contains('icon-selected')
    ) {
      return 'selected';
    }
    if (this.hasManualStatePair(element)) {
      return 'default';
    }
    if (this.iconHelperService.hasSelected(iconName) && this.shouldUseInteractiveSelectedState()) {
      return 'selected';
    }
    return 'default';
  }

  private bindInteractiveAncestor(): void {
    const element = this.host.nativeElement;
    const nextAncestor = element.closest(this.interactiveSelector) as HTMLElement | null;
    if (nextAncestor === this.interactiveAncestor) {
      return;
    }

    this.cleanupFns.forEach((cleanup) => cleanup());
    this.cleanupFns = [];
    this.interactiveObserver?.disconnect();
    this.interactiveObserver = undefined;
    this.interactiveAncestor = nextAncestor;

    const iconName = this.getIconName(element);
    if (
      !nextAncestor ||
      !iconName ||
      this.hasManualStatePair(element) ||
      !this.iconHelperService.hasSelected(iconName)
    ) {
      return;
    }

    const rerender = () => this.renderIcon();
    const onPointerEnter = () => rerender();
    const onPointerLeave = () => rerender();
    const onFocusIn = () => rerender();
    const onFocusOut = () => {
      if (!nextAncestor.matches(':focus, :focus-visible, :focus-within')) {
        this.suppressFocusSelectedState = false;
      }
      rerender();
    };
    const onTouchStart = () => {
      this.suppressFocusSelectedState = true;
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        this.suppressFocusSelectedState = true;
      }
    };
    const onPointerCancel = () => {
      this.suppressFocusSelectedState = false;
      rerender();
    };
    const onClick = () => {
      if (!this.suppressFocusSelectedState) {
        return;
      }
      queueMicrotask(() => {
        if (!this.host.nativeElement.isConnected) {
          return;
        }
        this.suppressFocusSelectedState = false;
        this.renderIcon();
      });
    };

    nextAncestor.addEventListener('pointerenter', onPointerEnter);
    nextAncestor.addEventListener('pointerleave', onPointerLeave);
    nextAncestor.addEventListener('focusin', onFocusIn);
    nextAncestor.addEventListener('focusout', onFocusOut);
    nextAncestor.addEventListener('touchstart', onTouchStart, { passive: true });
    nextAncestor.addEventListener('pointerdown', onPointerDown);
    nextAncestor.addEventListener('pointercancel', onPointerCancel);
    nextAncestor.addEventListener('click', onClick);

    this.cleanupFns.push(() => nextAncestor.removeEventListener('pointerenter', onPointerEnter));
    this.cleanupFns.push(() => nextAncestor.removeEventListener('pointerleave', onPointerLeave));
    this.cleanupFns.push(() => nextAncestor.removeEventListener('focusin', onFocusIn));
    this.cleanupFns.push(() => nextAncestor.removeEventListener('focusout', onFocusOut));
    this.cleanupFns.push(() => nextAncestor.removeEventListener('touchstart', onTouchStart));
    this.cleanupFns.push(() => nextAncestor.removeEventListener('pointerdown', onPointerDown));
    this.cleanupFns.push(() => nextAncestor.removeEventListener('pointercancel', onPointerCancel));
    this.cleanupFns.push(() => nextAncestor.removeEventListener('click', onClick));

    this.interactiveObserver = new MutationObserver(() => this.renderIcon());
    this.interactiveObserver.observe(nextAncestor, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled'],
    });
  }

  private hasManualStatePair(element: HTMLElement): boolean {
    return (
      element.classList.contains('default-icon') ||
      element.classList.contains('selected-icon') ||
      element.classList.contains('pen-icon-default') ||
      element.classList.contains('pen-icon-selected')
    );
  }

  private shouldUseInteractiveSelectedState(): boolean {
    const ancestor = this.interactiveAncestor;
    if (!ancestor) {
      return false;
    }

    if (
      ancestor.matches(
        '.app-button--selected, .active, .is-open, .is-selected, .state-selected, .icon-selected',
      )
    ) {
      return true;
    }

    if (this.isDisabledAncestor(ancestor)) {
      return false;
    }

    if (this.isHoverOnlyInteractiveAncestor(ancestor)) {
      return ancestor.matches(':hover');
    }

    if (ancestor.matches(':hover')) {
      return true;
    }

    if (this.suppressFocusSelectedState) {
      return false;
    }

    return ancestor.matches(':focus, :focus-visible, :focus-within');
  }

  private isDisabledAncestor(element: HTMLElement): boolean {
    return (
      element.matches(
        ':disabled, [disabled], [aria-disabled="true"], .disabled, .btn-disable, .app-button--disabled',
      ) || element.getAttribute('disabled') !== null
    );
  }

  private isHoverOnlyInteractiveAncestor(element: HTMLElement): boolean {
    return (
      element.classList.contains('icon-hover-only') ||
      element.getAttribute('data-icon-interaction') === 'hover-only'
    );
  }
}
